import React, { useState, useRef, useEffect } from 'react';
import './Internship.css';
import { INTERNSHIP_API_URL } from '../../config/api';
import { normalizeApiResponse } from '../../utils/api';
import { RECAPTCHA_SITE_KEY } from '../../config/recaptcha';

const Internship = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    courseCompleted: '',
    institute: '',
    university: '',
    phoneNumber: '',
    email: '',
    whyJoin: '',
    experience: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    // Wait for reCAPTCHA script to load
    const loadRecaptcha = () => {
      if (window.grecaptcha && recaptchaRef.current) {
        try {
          const widgetId = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            callback: (token) => {
              setRecaptchaToken(token);
            },
            'expired-callback': () => {
              setRecaptchaToken('');
            },
            'error-callback': () => {
              setRecaptchaToken('');
              console.error('reCAPTCHA error occurred');
            }
          });
          recaptchaRef.current.setAttribute('data-widget-id', widgetId);
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      } else if (!window.grecaptcha) {
        // Retry after a short delay if script hasn't loaded
        setTimeout(loadRecaptcha, 100);
      }
    };

    // Check if script is already loaded
    if (document.readyState === 'complete') {
      loadRecaptcha();
    } else {
      window.addEventListener('load', loadRecaptcha);
    }

    return () => {
      window.removeEventListener('load', loadRecaptcha);
    };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a PDF, DOC, or DOCX file.');
        e.target.value = '';
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB.');
        e.target.value = '';
        return;
      }
      
      setResumeFile(file);
    }
  };

  const validateForm = () => {
    const { firstName, lastName, courseCompleted, institute, university, phoneNumber, email, whyJoin } = formData;

    if (!firstName || !lastName || !courseCompleted || !institute || !university || !phoneNumber || !email || !whyJoin) {
      alert('Please fill in all required fields.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address.');
      return false;
    }

    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (!phoneRegex.test(phoneNumber)) {
      alert('Please enter a valid phone number.');
      return false;
    }

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      alert('Please complete the reCAPTCHA verification.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new FormData();
      
      // Map frontend keys to API expected keys (PascalCase)
      const apiFieldMap = {
        firstName: 'FirstName',
        lastName: 'LastName',
        courseCompleted: 'CourseCompleted',
        institute: 'Institute',
        university: 'University',
        phoneNumber: 'PhoneNumber',
        email: 'Email',
        whyJoin: 'WhyJoin',
        experience: 'Experience'
      };

      Object.keys(formData).forEach(key => {
        const apiKey = apiFieldMap[key] || key;
        formDataToSend.append(apiKey, formData[key]);
      });
      
      // Add reCAPTCHA token
      if (!recaptchaToken) {
        setSubmitMessage('Please complete the reCAPTCHA verification.');
        setIsLoading(false);
        return;
      }
      formDataToSend.append('RecaptchaToken', recaptchaToken);
      
      // Add resume file if selected
      if (resumeFile) {
        formDataToSend.append('resume', resumeFile);
      }

      // Debug: Log the form data being sent
      console.log('Form data being sent:', formData);
      console.log('Resume file:', resumeFile);

      const response = await fetch(`${INTERNSHIP_API_URL}/apply`, {
        method: 'POST',
        body: formDataToSend
      });

      // Resilient parsing: handle non-JSON error bodies
      const contentType = response.headers.get('content-type') || '';
      let result;
      if (contentType.includes('application/json')) {
        try {
          const json = await response.json();
          result = normalizeApiResponse(json);
        } catch (e) {
          result = { success: response.ok, message: `Request ${response.ok ? 'succeeded' : 'failed'} (invalid JSON response)` };
        }
      } else {
        const text = await response.text();
        result = { success: response.ok, message: text || `HTTP ${response.status} ${response.statusText}` };
      }

      if (result.success) {
        setIsSubmitted(true);
        setSubmitMessage(result.message || 'Application submitted successfully!');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          courseCompleted: '',
          institute: '',
          university: '',
          phoneNumber: '',
          email: '',
          whyJoin: '',
          experience: ''
        });
        setResumeFile(null);
        setRecaptchaToken('');
        // Reset reCAPTCHA
        if (window.grecaptcha && recaptchaRef.current) {
          const widgetId = recaptchaRef.current.getAttribute('data-widget-id');
          if (widgetId) {
            window.grecaptcha.reset(widgetId);
          }
        }
      } else {
        setSubmitMessage(result.message || 'Submission failed. Please check your details and try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage('An error occurred while submitting your application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="internship-page">
      <div className="container">
        <div className="form-header">
          <h1>Internship Application</h1>
          <p>Join our team and work on cutting-edge AI projects</p>
        </div>

        {isSubmitted && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i> {submitMessage}
          </div>
        )}

        {submitMessage && !isSubmitted && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {submitMessage}
          </div>
        )}

        <form className="application-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Personal Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Educational Background</h2>
            <div className="form-group">
              <label htmlFor="courseCompleted">Course Completed *</label>
              <input
                type="text"
                id="courseCompleted"
                name="courseCompleted"
                placeholder="e.g., B.Tech Computer Science"
                value={formData.courseCompleted}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="institute">Institute *</label>
                <input
                  type="text"
                  id="institute"
                  name="institute"
                  placeholder="e.g., IIT Delhi"
                  value={formData.institute}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="university">University *</label>
                <input
                  type="text"
                  id="university"
                  name="university"
                  placeholder="e.g., Delhi University"
                  value={formData.university}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Contact Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder="+91 98765 43210"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email ID *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Additional Information</h2>
            <div className="form-group">
              <label htmlFor="whyJoin">Why do you want to join our internship program? *</label>
              <textarea
                id="whyJoin"
                name="whyJoin"
                placeholder="Tell us about your interest in AI and what you hope to learn..."
                value={formData.whyJoin}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>
            <div className="form-group">
              <label htmlFor="experience">Relevant Experience (if any)</label>
              <textarea
                id="experience"
                name="experience"
                placeholder="Describe any relevant projects, coursework, or experience with AI/ML technologies..."
                value={formData.experience}
                onChange={handleInputChange}
              ></textarea>
            </div>
            <div className="form-group">
              <label htmlFor="resume">Resume/CV (PDF, DOC, DOCX - Max 5MB)</label>
              <input
                type="file"
                id="resume"
                name="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
              />
              {resumeFile && (
                <div className="file-info">
                  <i className="fas fa-file"></i> {resumeFile.name} ({(resumeFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h2>Verification</h2>
            <div className="form-group">
              <label>Please verify you're not a robot *</label>
              <div 
                ref={recaptchaRef}
                style={{ marginTop: '10px' }}
              ></div>
            </div>
          </div>

          <div className="submit-section">
            <button type="submit" className="btn-submit" disabled={isSubmitted || isLoading}>
              <i className="fas fa-paper-plane"></i>
              {isLoading ? 'Submitting...' : isSubmitted ? 'Application Submitted' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Internship;
